import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { IndianRupee, Download, Filter, X, CreditCard, AlertTriangle, CheckCircle, Clock, ChevronDown, FileSpreadsheet, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTS = [
  { value: 'pending', label: 'Pending' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
];

const STATUS_BADGE = {
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  partially_paid: 'bg-amber-100 text-amber-800',
  pending: 'bg-slate-100 text-slate-600',
};

const fmtRupee = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

export default function PaymentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [masters, setMasters] = useState({ courses: [], users: [], modes: [], months: [] });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/masters/courses'),
      API.get('/users'),
      API.get('/masters/payment-modes'),
      API.get('/payments/months'),
    ]).then(([c, u, m, mo]) => {
      setMasters({ courses: c.data, users: u.data, modes: m.data, months: mo.data });
    }).catch(() => {});
  }, []);

  const fetchReport = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.course) params.course = filters.course;
    if (filters.status) params.status = filters.status;
    if (filters.mode) params.mode = filters.mode;
    if (filters.month) params.month = filters.month;
    if (filters.counsellor) params.counsellor = filters.counsellor;
    API.get('/payments/report', { params })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load payments'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const activeFilters = Object.keys(filters).filter(k => filters[k]);

  const clearFilters = () => setFilters({});

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.course) params.append('course', filters.course);
      if (filters.status) params.append('status', filters.status);
      if (filters.mode) params.append('mode', filters.mode);
      if (filters.month) params.append('month', filters.month);
      if (filters.counsellor) params.append('counsellor', filters.counsellor);
      const resp = await API.get(`/payments/export/${type}?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      const names = { summary: 'payment_summary', schedule: 'payment_schedule', outstanding: 'outstanding_payments', 'course-wise': 'course_wise_collection' };
      a.download = `${names[type] || type}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const payments = data?.payments || [];

  return (
    <div className="p-6 space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>Payments</h1>
          <p className="text-sm text-slate-500 mt-1">Track fee collection across all students</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="export-dropdown-btn" disabled={exporting}>
              <Download size={14} /> Export <ChevronDown size={12} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => handleExport('summary')} data-testid="export-summary-btn">
              <FileSpreadsheet size={14} className="mr-2" /> Payment Summary
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('schedule')} data-testid="export-schedule-btn">
              <FileSpreadsheet size={14} className="mr-2" /> Schedule Details
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('outstanding')} data-testid="export-outstanding-btn">
              <FileSpreadsheet size={14} className="mr-2" /> Outstanding Report
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('course-wise')} data-testid="export-course-wise-btn">
              <FileSpreadsheet size={14} className="mr-2" /> Course-wise Report
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-sm text-slate-500">
          <Filter size={14} /> Filters:
        </div>
        <Select value={filters.course || "all"} onValueChange={v => setFilters(p => ({ ...p, course: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-course"><SelectValue placeholder="All Courses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Courses</SelectItem>
            {masters.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status || "all"} onValueChange={v => setFilters(p => ({ ...p, status: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUS_OPTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.mode || "all"} onValueChange={v => setFilters(p => ({ ...p, mode: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-mode"><SelectValue placeholder="All Modes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            {masters.modes.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.month || "all"} onValueChange={v => setFilters(p => ({ ...p, month: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-month"><SelectValue placeholder="All Months" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {masters.months.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.counsellor || "all"} onValueChange={v => setFilters(p => ({ ...p, counsellor: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-40 h-9 text-xs" data-testid="filter-counsellor"><SelectValue placeholder="All Counsellors" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Counsellors</SelectItem>
            {masters.users.filter(u => ['admin', 'counsellor', 'admission_head'].includes(u.role)).map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilters.length > 0 && (
          <Button variant="ghost" size="sm" className="text-xs text-slate-500 gap-1" onClick={clearFilters} data-testid="clear-filters-btn">
            <X size={12} /> Clear
          </Button>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Students', value: summary.count || 0, icon: CreditCard, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Total Fees', value: fmtRupee(summary.total_fees), icon: IndianRupee, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Scheduled', value: fmtRupee(summary.total_scheduled), icon: Clock, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Collected', value: fmtRupee(summary.total_paid), icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Outstanding', value: fmtRupee(summary.total_outstanding), icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50' },
        ].map(kpi => (
          <div key={kpi.label} className="kpi-card" data-testid={`payment-kpi-${kpi.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${kpi.bg}`}><kpi.icon size={16} className={kpi.color} /></div>
            </div>
            <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{kpi.value}</p>
            <p className="text-xs text-slate-500 mt-1 font-medium">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Payment Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">
            Payment Records {activeFilters.length > 0 && <span className="text-xs font-normal text-slate-400 ml-2">({payments.length} results)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Lead ID</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Student</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Course</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase">Counsellor</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Total Fees</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Scheduled</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Paid</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Balance</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="px-3 py-12 text-center text-sm text-slate-400">Loading...</td></tr>
                ) : payments.length === 0 ? (
                  <tr><td colSpan={10} className="px-3 py-12 text-center text-sm text-slate-400">No payment records found</td></tr>
                ) : payments.map(p => (
                  <tr key={p.payment_id} className="border-b hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/leads/${p.lead_id}`)} data-testid={`payment-row-${p.lead_uid}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{p.lead_uid}</td>
                    <td className="px-3 py-2.5">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{p.lead_name}</p>
                        <p className="text-xs text-slate-400">{p.phone || p.email}</p>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{p.course_name}</td>
                    <td className="px-3 py-2.5 text-slate-600 text-xs">{p.counsellor_name || '—'}</td>
                    <td className="px-3 py-2.5 text-right font-medium">{fmtRupee(p.total_fees)}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{fmtRupee(p.total_scheduled)}</td>
                    <td className="px-3 py-2.5 text-right text-green-700 font-medium">{fmtRupee(p.total_paid)}</td>
                    <td className="px-3 py-2.5 text-right text-amber-700">{fmtRupee(p.total_left)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <Badge className={`text-xs capitalize ${STATUS_BADGE[p.status] || STATUS_BADGE.pending}`}>
                        {(p.status || 'pending').replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <ExternalLink size={14} className="text-slate-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
